import React, { useState, useEffect, useCallback } from 'react';
import {TOUCHPOINTSURL} from '../constants.js';
import axios from 'axios';


/**
 * A form component for reporting a problem.
 *
 * @param {Object} props - The props object containing setModalError.
 * @param {function} props.setModalError - Function to set an error in the modal.
 * @returns {JSX.Element} - JSX element representing the report problem form.
 */
export function ReportProblemForm(props){
    const {setModalError, submitted, setSubmitted} = props;
    const [problemType, setProblemType] = useState('');
    const [issueDescription, setIssueDescription] = useState('');
    const [additionalInfo, setAdditionalInfo] = useState({});
    const [suggestionCategory, setSuggestionCategory] = useState('');
    const [reportData, setReportData] = useState(null);


    const availableOptions = [
                               {label:'Select', value:'', placeholder:''},
                               {label:'Broken Link', value:'Broken Link', placeholder:'Please paste the broken link...'},
                               {label:'Sample Code Not Working', value:'Sample Code Not Working', placeholder:'Please paste the code...'},
                               {label:'Error in Text', value:'Error in Text', placeholder:'Please describe the error...'},
                               {label:'Suggested Addition to the Page', value:'Suggested Addition to the Page', placeholder:'Please describe the request...'},
                               {label:'General Feedback', value:'General Feedback', placeholder:'Please describe the problem...'},
                             ]

    const suggestionOptions = [
                               {label:'Select', value:'', placeholder:''},
                               {label:'Code', value:'Code', placeholder:'Please paste the code...'},
                               {label:'Link', value:'Link', placeholder:'Please paste the link...'},
                               {label:'Related Resource', value:'Related Resource', placeholder:'Please describe the resource...'},
                               {label:'Feature Request', value:'Feature Request', placeholder:'Please describe the request...'},
                               {label:'Other(Please specify)', value:'etc', placeholder:'Please write to us...'},
                             ]

    const optionIndexes = availableOptions.reduce((acc, obj, index) => {
                              acc[obj.label] = index;
                              return acc;
                          }, {});

    const updateReportData = useCallback((problemType, issueDescription, suggestionCategory, additionalInfo) => {
        setReportData({
            reportType: problemType,
            description: issueDescription,
            possibleRemedy: additionalInfo,
            suggestionCategory,
        });
    }, []);

    useEffect(() => {updateReportData(problemType, issueDescription, suggestionCategory, additionalInfo)}, [problemType, issueDescription, suggestionCategory, additionalInfo, updateReportData]);

    const sendReportData = async (reportData, apiKey) => {
        const formData = {
            fba_location_code: 'your_location_code_value',
            fba_directive: 'your_directive_value',
        };

        const answerField = `answer_${String(optionIndexes[reportData.reportType]).padStart(2, '0')}`;
        const answerReportType = `${reportData.reportType}`;
        const answerSuggestionCategory = `${reportData.suggestionCategory ? "\nSuggestion Category: " + reportData.suggestionCategory : ""}`;
        const answerDescription = `\n${"Description:\n"+reportData.description}`;
        const answerRemedy      = `\n${reportData?.possibleRemedy[reportData.reportType] ? "\nSuggested Remedy:\n"+reportData.possibleRemedy[reportData.reportType] : ""}`;
        const answerUrl = `\nURL: ${window.location.href}`;
        const answerValue = answerReportType + answerSuggestionCategory + answerDescription + answerRemedy + answerUrl;
        formData[answerField] = answerValue;


        try {
            await axios.post(TOUCHPOINTSURL, formData);
        } catch (error) {
            throw error;
        }
    };

    const handleSubmit = async (event) => {
        event.preventDefault();

        try{
            setModalError(null);

            //Prepare report
            updateReportData();

            //Send to TouchPoint
            await sendReportData(reportData);
            setSubmitted(true);
        } catch(error) {
            setModalError({status: error.status, message: error.message});
        }
    };

    const renderAdditionalOptionsPrefix = () => {
        if (problemType === 'Suggested Addition to the Page') {
            return getAdditionalSuggestions(suggestionCategory, setSuggestionCategory, suggestionOptions);
        } else if (problemType === 'General Feedback') {
            return getAdditionalSuggestions(null, null, []);
        }
        return null;
    }

    const renderAdditionalOptionsSuffix = () => {
        if (problemType === 'Broken Link') {
            return getBrokenLinkSuggestion(additionalInfo, handleAdditionalInfoChange);
        } else if (problemType === 'Error in Text') {
            return getTextErrorSuggestion(additionalInfo, handleAdditionalInfoChange);
        }
        return null;
    };

    const handleAdditionalInfoChange = (field, value) => {
        const newObj = {};
        newObj[field] = value;
        setAdditionalInfo({...additionalInfo, ...newObj})

    }

    return (
      <div>
        <div className="reportProblemForm">
            {!submitted ? (
                <form onSubmit={handleSubmit}>
                    <h2>What's happening?</h2>
                    <div className="formGroup">
                        <label htmlFor="problemType">Select Topic:</label>
                        <select
                            id="problemType"
                            value={problemType}
                            onChange={(e) => setProblemType(e.target.value)}
                            required
                        >
                            {availableOptions.map((el, i) => {return <option value={el.value} key={i + 300}>{el.label}</option>})}
                        </select>
                    </div>
                    {renderAdditionalOptionsPrefix()}
                    <div className="formGroup">
                        <label htmlFor="issueDescription">Please describe more</label>
                        <textarea
                            id="issueDescription"
                            value={issueDescription}
                            onChange={(e) => setIssueDescription(e.target.value)}
                            placeholder={(optionIndexes[problemType] && availableOptions[optionIndexes[problemType]].placeholder) || 'Please describe the issue...'}
                            required
                        />
                    </div>
                    {renderAdditionalOptionsSuffix()}
                    <button type="submit" className="submitButton">Submit Report</button>
                </form>
            ) : (
                <p className="confirmationMessage">Thank you for reporting the issue. We will review it to improve the system.</p>
            )}
        </div>
      </div>
    );
};


/**
 * Generates a broken link problem section of a report problem form.
 *
 * @param {Object} additionalInfo - Object containing additional information.
 * @param {string} additionalInfo.brokenLink - The URL of the broken link. state value.
 * @param {function} handleAdditionalInfoChange - Function to handle changes in additional information.
 * @returns {JSX.Element} - JSX element representing the suggestion form.
 */
function getBrokenLinkSuggestion(additionalInfo, handleAdditionalInfoChange) {
    return (
             <div className="formGroup">
               <label htmlFor="updatedLink">Suggest an Updated Link (Optional):</label>
               <textarea
                 id="updatedLink"
                 value={additionalInfo.brokenLink}
                 onChange={(e) => handleAdditionalInfoChange('Broken Link', e.target.value)}
                 placeholder="Paste the updated link here..." />
             </div>
           );
}

/**
 * Generates an error in text suggestion section of a report problem form.
 * 
 * @param {Object} additionalInfo - Object containing additional information.
 * @param {string} additionalInfo.errorInText - The text containing the error.
 * @param {function} handleAdditionalInfoChange - Function to handle changes in additional information.
 * @returns {JSX.Element} - JSX element representing the suggestion form.
 */
function getTextErrorSuggestion(additionalInfo, handleAdditionalInfoChange) {
    return (
             <div className="formGroup">
               <label htmlFor="updatedText">Suggest a Fix (Optional):</label>
               <textarea
                 id="updatedText"
                 value={additionalInfo.errorInText}
                 onChange={(e) => handleAdditionalInfoChange('Error in Text', e.target.value)}
                 placeholder="Please write your suggestion here..." />
             </div>
           );
}


/**
 * Generates a section of a feedback form that deal with suggestions from users.
 * 
 * @param {string} suggestionCategory - The current suggestion category.
 * @param {function} setSuggestionCategory - Function to set the suggestion category.
 * @param {Array<Object>} suggestionOptions - Array of suggestion options with value and label properties.
 * @returns {JSX.Element} - JSX element representing the form to select additional suggestions.
 */
function getAdditionalSuggestions(suggestionCategory, setSuggestionCategory, suggestionOptions) {
    return (
             <>
               {suggestionOptions && suggestionOptions.length > 0 && <div className="formGroup">
                 <label htmlFor="feedbackCategory">Feedback Category:</label>
                 <select
                   id="feedbackCategory"
                   value={suggestionCategory}
                   onChange={(e) => setSuggestionCategory(e.target.value)}
                   required
                 >
                   {suggestionOptions.map((el) => <option value={el.value}>{el.label}</option>)}
                 </select>
               </div>}
              </>
           );
}
